import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { LifeBuoy, Plus, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const STATUS_LABEL = {
  open: { label: "Open", color: "text-blue-400 bg-blue-400/10" },
  awaiting_support: { label: "Awaiting Reply", color: "text-yellow-400 bg-yellow-400/10" },
  awaiting_customer: { label: "Awaiting You", color: "text-orange-400 bg-orange-400/10" },
  resolved: { label: "Resolved", color: "text-green-400 bg-green-400/10" },
  closed: { label: "Closed", color: "text-gray-400 bg-gray-400/10" },
};

const SupportTicketsPage = () => {
  const { user, token } = useAuth();
  const { ticketId } = useParams();
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ subject: "", body: "" });
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadList = async () => {
    if (!user) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/support/tickets`, { headers });
      setTickets(r.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  const loadTicket = async (id) => {
    try {
      const r = await axios.get(`${API}/support/tickets/${id}`, { headers });
      setActiveTicket(r.data);
    } catch {
      toast.error("Failed to load ticket");
    }
  };

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  useEffect(() => {
    if (ticketId) loadTicket(ticketId);
    else setActiveTicket(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const submitNew = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const r = await axios.post(`${API}/support/tickets`, newForm, { headers });
      toast.success("Ticket created");
      setNewForm({ subject: "", body: "" });
      setShowNew(false);
      setTickets((prev) => [r.data, ...prev]);
    } catch {
      toast.error("Failed to create ticket");
    } finally {
      setSending(false);
    }
  };

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim() || !activeTicket) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets/${activeTicket.ticket_id}/reply`, { body: replyBody }, { headers });
      setReplyBody("");
      loadTicket(activeTicket.ticket_id);
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const closeTicket = async () => {
    if (!activeTicket || !window.confirm("Close this ticket?")) return;
    try {
      await axios.put(`${API}/support/tickets/${activeTicket.ticket_id}/status`, { status: "closed" }, { headers });
      loadTicket(activeTicket.ticket_id);
      toast.success("Ticket closed");
    } catch {
      toast.error("Failed to close");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 px-4 text-center">
        <div className="max-w-md mx-auto py-16">
          <p className="text-gray-400 mb-4">Log in to access support.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-6 py-3 rounded-sm font-bold uppercase tracking-wider">Log In</Link>
        </div>
      </div>
    );
  }

  const inputCls = "w-full bg-[#1A1A1A] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:border-[#D62828] focus:outline-none";

  // Single ticket view
  if (activeTicket) {
    const statusInfo = STATUS_LABEL[activeTicket.status] || STATUS_LABEL.open;
    const canClose = !["resolved", "closed"].includes(activeTicket.status);
    return (
      <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="ticket-detail">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Link to="/support" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6"><ArrowLeft className="w-4 h-4" /> All Tickets</Link>
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-6">
            <div className="flex items-start justify-between gap-4 mb-2">
              <h1 className="font-heading text-2xl tracking-wide">{activeTicket.subject}</h1>
              <span className={`px-3 py-1 rounded-sm text-xs uppercase tracking-wide font-bold ${statusInfo.color}`}>{statusInfo.label}</span>
            </div>
            <p className="text-gray-500 text-xs">Opened {new Date(activeTicket.created_at).toLocaleString()}</p>
          </div>

          <div className="space-y-4 mb-6" data-testid="ticket-messages">
            {(activeTicket.messages || []).map((m) => (
              <div key={m.message_id} className={`p-4 rounded-sm border ${m.from === 'support' ? 'bg-[#1A1413] border-[#D62828]/20 ml-0 mr-8' : 'bg-[#121212] border-white/10 ml-8 mr-0'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold uppercase tracking-wide ${m.from === 'support' ? 'text-[#D4AF37]' : 'text-gray-400'}`}>
                    {m.from === 'support' ? '★ Cut Above Support' : m.author_name}
                  </span>
                  <span className="text-gray-500 text-xs">{new Date(m.created_at).toLocaleString()}</span>
                </div>
                <p className="text-gray-200 text-sm whitespace-pre-line">{m.body}</p>
              </div>
            ))}
          </div>

          {activeTicket.status !== "closed" && (
            <form onSubmit={submitReply} className="bg-[#121212] border border-white/10 rounded-sm p-4" data-testid="reply-form">
              <textarea value={replyBody} onChange={(e) => setReplyBody(e.target.value)} rows={3} placeholder="Type your reply..." className={inputCls} required />
              <div className="flex gap-2 mt-3">
                <button type="submit" disabled={sending} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-wide flex items-center gap-1 disabled:opacity-50" data-testid="send-reply-btn">
                  <Send className="w-3 h-3" /> {sending ? 'Sending...' : 'Send'}
                </button>
                {canClose && (
                  <button type="button" onClick={closeTicket} className="text-gray-400 hover:text-red-400 text-xs px-3 py-2 uppercase tracking-wide" data-testid="close-ticket-btn">Close Ticket</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="support-tickets-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl tracking-wide">SUPPORT TICKETS</h1>
            <p className="text-gray-500 text-sm mt-1">{tickets.length} ticket{tickets.length === 1 ? '' : 's'}</p>
          </div>
          {!showNew && (
            <button onClick={() => setShowNew(true)} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-4 py-2 rounded-sm text-sm font-bold uppercase tracking-wide flex items-center gap-2" data-testid="new-ticket-btn">
              <Plus className="w-4 h-4" /> New Ticket
            </button>
          )}
        </div>

        {showNew && (
          <form onSubmit={submitNew} className="bg-[#121212] border border-white/10 rounded-sm p-5 mb-6" data-testid="new-ticket-form">
            <input className={`${inputCls} mb-3`} placeholder="Subject (e.g. Missing item in order #...)" value={newForm.subject} onChange={(e) => setNewForm({ ...newForm, subject: e.target.value })} required maxLength={120} data-testid="ticket-subject" />
            <textarea className={inputCls} rows={5} placeholder="Describe the issue in detail..." value={newForm.body} onChange={(e) => setNewForm({ ...newForm, body: e.target.value })} required data-testid="ticket-body" />
            <div className="flex gap-2 mt-3">
              <button type="submit" disabled={sending} className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-5 py-2 rounded-sm text-xs font-bold uppercase tracking-wide disabled:opacity-50" data-testid="submit-ticket-btn">
                {sending ? 'Submitting...' : 'Submit Ticket'}
              </button>
              <button type="button" onClick={() => setShowNew(false)} className="text-gray-400 hover:text-white text-xs px-3 py-2">Cancel</button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D62828] mx-auto"></div>
        ) : tickets.length === 0 && !showNew ? (
          <div className="text-center py-16">
            <LifeBuoy className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400">No tickets yet. We're here when you need us.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((t) => {
              const s = STATUS_LABEL[t.status] || STATUS_LABEL.open;
              return (
                <Link key={t.ticket_id} to={`/support/${t.ticket_id}`} className="block bg-[#121212] border border-white/5 hover:border-[#D62828]/50 rounded-sm p-4 transition-colors" data-testid={`ticket-${t.ticket_id}`}>
                  <div className="flex items-start justify-between gap-4 mb-1">
                    <p className="text-white font-semibold text-sm">{t.subject}</p>
                    <span className={`flex-shrink-0 px-2 py-0.5 rounded-sm text-xs uppercase tracking-wide font-bold ${s.color}`}>{s.label}</span>
                  </div>
                  <p className="text-gray-500 text-xs">
                    {(t.messages || []).length} message{(t.messages || []).length === 1 ? '' : 's'} · Updated {new Date(t.last_message_at).toLocaleDateString()}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportTicketsPage;
